import Spacer from '@berlitz/spacer'
import Close from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/33-Form-Validation/Close'
import SchoolTeacher from '@berlitz/streamline-icons/lib/regular/36-School-Learning/01-School/SchoolTeacher'
import InstructorProfile from '@classes/InstructorProfile'
import HR from '@components/HR'
import MenuTab, { MenuTabItem } from '@components/MenuTab'
import Scroller from '@components/Scroller'
import { InstructorSearchResultInfo } from '@interfaces/Instructor'
import { MenuTabBox } from '@layouts/LearningPath/DetailTabs/style'
import { isEqual, sortBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import Skeleton from 'react-loading-skeleton'
import AvatarWithName from '../AvatarWithName'
import Carousel from '../Carousel'
import { useBlockedInstructors, useFave } from '../context'
import EmptyInstructors from '../EmptyInstructors'
import { DesktopView, MobileView } from '../style'
import menu from './menu'
import {
  CloseBtn,
  FixedButton,
  MMIButton,
  MMIContent,
  MMIFixed,
  MMIHeader,
  MMISubContent,
  StyledH2,
  Tab,
} from './style'
import BlockedInstructorsCarousel from './BlockedInstructorsCarousel'
import SessionPreferences from './SessionPreferences'
import { StoreProps } from '@interfaces/StoreState'
import { useSelector } from 'react-redux'

interface ManageInstructorViewProps {
  tab?: number
  loading?: boolean
  pastInstructors?: InstructorSearchResultInfo[]
  faveInstructors?: InstructorSearchResultInfo[]
  blockedInstructors?: InstructorSearchResultInfo[]
  goToInstructor: (a: string, b?: string) => void
  isBulkOnlyFeature?: boolean
  programId?: string
}

const ManageInstructorView: React.FC<ManageInstructorViewProps> = ({
  loading,
  pastInstructors = [],
  tab = 1,
  goToInstructor,
  isBulkOnlyFeature,
  programId,
}) => {
  const fave = useFave()
  const blocked = useBlockedInstructors()
  const blockedInstructors = blocked.blockedInstructors
  const buttonLabel = fave.isEditMode ? 'Done' : 'Edit'
  const [at, setAt] = useState(tab)
  const [showMMI, setShowMMI] = useState(false)
  const [editFavedInstructors, setEditFavedInstructors] = useState<InstructorSearchResultInfo[]>([])

  const { userPermissions } = useSelector(({ permissions, session }: StoreProps) => ({
    userPermissions: permissions.permissions,
    isAdmin: session.isAdmin,
  }))
  const SessionPrefFlag = userPermissions['flag::SessionPreference-feature']?.show

  useEffect(() => {
    setAt(tab || loading ? 1 : at)
  }, [tab, loading])

  useEffect(() => {
    if (fave.isEditMode) {
      setEditFavedInstructors(fave.faveInstructors)
    } else {
      if (!isEqual(editFavedInstructors, fave.faveInstructors)) {
        fave.setFaveInstructors(editFavedInstructors)
        fave.deleteRelatedInstructors({
          variables: fave.getPayload(
            'delete',
            fave.faveInstructors.filter(
              ({ InstructorId }) => !editFavedInstructors.some((unFaved) => unFaved.InstructorId === InstructorId)
            )
          ),
        })
      }
    }
  }, [fave.isEditMode])

  const onClickIcon = (instructor: InstructorSearchResultInfo) => {
    if (fave.isEditMode) {
      setEditFavedInstructors(
        sortBy(
          editFavedInstructors.filter(({ InstructorId }) => {
            return InstructorId !== instructor.InstructorId
          }),
          ['Name']
        )
      )
    } else {
      goToInstructor('profile', instructor.InstructorId)
    }
  }

  const onClickFavedAvatar = (instructor: InstructorSearchResultInfo) => {
    !fave.isEditMode && goToInstructor('profile', instructor.InstructorId)
  }

  return (
    <>
      <DesktopView>
        <Scroller>
          <MenuTabBox>
            <MenuTab activeTab={at}>
              {menu
                .filter(({ tab }) => (tab === 4 ? SessionPrefFlag : blocked.allowed || tab !== 3))
                .map((item) => (
                  <MenuTabItem key={item.tab}>
                    {loading ? (
                      <Skeleton width="130px" height="18px" />
                    ) : (
                      <Tab onClick={() => setAt(item.tab)}>{item.child}</Tab>
                    )}
                  </MenuTabItem>
                ))}
            </MenuTab>
          </MenuTabBox>
        </Scroller>
        {at === 1 ? (
          loading ? (
            <>
              <Spacer size="sm" />
              <Carousel
                loading
                items={[1, 2, 3, 4, 5].map((i) => (
                  <AvatarWithName key={i} loading />
                ))}
              />
            </>
          ) : (fave.isEditMode && editFavedInstructors.length) || fave.faveInstructors.length ? (
            <>
              <Spacer size="sm" />
              <Carousel
                items={(fave.isEditMode ? editFavedInstructors : fave.faveInstructors)?.map((instructor) => {
                  const Profile = new InstructorProfile(instructor)
                  return (
                    <AvatarWithName
                      key={`fave-instructor-${instructor.InstructorId}`}
                      image={Profile.Avatar}
                      onClickAvatar={() => onClickFavedAvatar(instructor)}
                      onClickName={() => onClickFavedAvatar(instructor)}
                      onClickIcon={() => onClickIcon(instructor)}
                      name={instructor.Name}
                      fave
                      isEditMode={fave.isEditMode}
                    />
                  )
                })}
                isEditMode={fave.isEditMode}
                hideEdit={!fave.enableActions}
              />
            </>
          ) : (
            <EmptyInstructors />
          )
        ) : at === 2 ? (
          pastInstructors?.length ? (
            <>
              <Spacer size="sm" />
              <Carousel
                items={pastInstructors?.map((instructor) => {
                  const Profile = new InstructorProfile(instructor)
                  return (
                    <AvatarWithName
                      key={`past-instructor-${instructor.InstructorId}`}
                      image={Profile.Avatar}
                      onClickAvatar={() => goToInstructor('profile', instructor.InstructorId)}
                      onClickName={() => goToInstructor('profile', instructor.InstructorId)}
                      name={instructor.Name}
                    />
                  )
                })}
                hideEdit
              />
            </>
          ) : (
            <EmptyInstructors past />
          )
        ) : at === 3 ? (
          blockedInstructors.length ? (
            <>
              <Spacer size="sm" />
              <BlockedInstructorsCarousel
                onClickAvatar={(instructor) => onClickFavedAvatar(instructor)}
                onClickName={(instructor) => onClickFavedAvatar(instructor)}
                onClickIcon={(instructor) => onClickIcon(instructor)}
                isEditMode={blocked.isEditMode}
                isBulkOnlyFeature={isBulkOnlyFeature}
              />
            </>
          ) : (
            <EmptyInstructors blocked />
          )
        ) : at === 4 ? (
          <SessionPreferences programId={programId} />
        ) : null}
        <Spacer size="xs" />
        <HR />
        <Spacer size="sm" />
      </DesktopView>
      {!loading && (
        <MobileView>
          <FixedButton show={!showMMI} onClick={() => setShowMMI(true)}>
            <SchoolTeacher />
          </FixedButton>
          <MMIFixed show={showMMI}>
            <MMIHeader>
              <StyledH2>
                <FormattedMessage id={'My instructors'} defaultMessage={'My instructors'} />
              </StyledH2>
              <CloseBtn onClick={() => setShowMMI(false)}>
                <Close />
              </CloseBtn>
            </MMIHeader>
            <MMIContent>
              <Spacer size="md" />
              <Scroller>
                <MenuTabBox>
                  <MenuTab activeTab={at}>
                    {menu
                      .filter(({ tab }) => (tab === 4 ? SessionPrefFlag : blocked.allowed || tab !== 3))
                      .map((item) => (
                        <MenuTabItem key={item.tab}>
                          <Tab onClick={() => setAt(item.tab)}>{item.child}</Tab>
                        </MenuTabItem>
                      ))}
                  </MenuTab>
                </MenuTabBox>
              </Scroller>
              <Spacer size="md" />
              {at === 1 ? (
                (fave.isEditMode ? editFavedInstructors : fave.faveInstructors).length ? (
                  <>
                    <Spacer size="sm" />
                    <Spacer size="sm" />
                    <MMISubContent>
                      {(fave.isEditMode ? editFavedInstructors : fave.faveInstructors)?.map((instructor) => {
                        const Profile = new InstructorProfile(instructor)
                        return (
                          <AvatarWithName
                            key={instructor.InstructorId}
                            image={Profile.Avatar}
                            onClickAvatar={() => onClickFavedAvatar(instructor)}
                            onClickName={() => onClickFavedAvatar(instructor)}
                            onClickIcon={() => onClickIcon(instructor)}
                            name={instructor.Name}
                            fave
                            isEditMode={fave.isEditMode}
                          />
                        )
                      })}
                      {
                        // add fillers so layout won't break
                        (fave.isEditMode ? editFavedInstructors : fave.faveInstructors).length < 3 &&
                          [1, 2, 3]?.map((i) => <div key={i}></div>)
                      }
                    </MMISubContent>
                    <Spacer size="md" />
                    {tab === 1 && fave.enableActions ? (
                      <MMIButton outline onClick={() => fave.setIsEditMode(!fave.isEditMode)}>
                        <FormattedMessage id={buttonLabel} defaultMessage={buttonLabel} />
                      </MMIButton>
                    ) : tab === 3 && blocked.enableActions ? (
                      <MMIButton outline onClick={() => blocked.setIsEditMode(!blocked.isEditMode)}>
                        <FormattedMessage id={buttonLabel} defaultMessage={buttonLabel} />
                      </MMIButton>
                    ) : null}
                  </>
                ) : (
                  <EmptyInstructors />
                )
              ) : at === 2 ? (
                pastInstructors?.length ? (
                  <>
                    <Spacer size="sm" />
                    <div>
                      <MMISubContent>
                        {pastInstructors?.map((instructor) => {
                          const Profile = new InstructorProfile(instructor)
                          return (
                            <AvatarWithName
                              key={instructor.InstructorId}
                              image={Profile.Avatar}
                              onClickAvatar={() => goToInstructor('profile', instructor.InstructorId)}
                              onClickName={() => goToInstructor('profile', instructor.InstructorId)}
                              name={instructor.Name}
                            />
                          )
                        })}
                        {
                          // add fillers so layout won't break
                          pastInstructors.length < 3 && [1, 2, 3]?.map((i) => <div key={i}></div>)
                        }
                      </MMISubContent>
                    </div>

                    <Spacer size="lg" />
                  </>
                ) : (
                  <EmptyInstructors past />
                )
              ) : at === 3 ? (
                blockedInstructors.length ? (
                  <>
                    <Spacer size="sm" />
                    <BlockedInstructorsCarousel
                      onClickAvatar={(instructor) => onClickFavedAvatar(instructor)}
                      onClickName={(instructor) => onClickFavedAvatar(instructor)}
                      onClickIcon={(instructor) => onClickIcon(instructor)}
                      isEditMode={blocked.isEditMode}
                      isBulkOnlyFeature={isBulkOnlyFeature}
                    />
                  </>
                ) : (
                  <EmptyInstructors blocked />
                )
              ) : at === 4 ? (
                <SessionPreferences programId={programId} />
              ) : null}
              <Spacer size="md" />
            </MMIContent>
          </MMIFixed>
        </MobileView>
      )}
    </>
  )
}

export default ManageInstructorView
